var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaidIcon from "@mui/icons-material/Paid";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Alert, Avatar, Box, Button, CircularProgress, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, Switch, TextField, Typography, } from "@mui/material";
import { useAdminCoins } from "features/admin/coins/model/hooks/useAdminCoins";
import { useNavigate, useParams } from "react-router";
import { FONT, T } from "../../../admin.theme";
import { AdminButton } from "../../../ui";
import { useAdminToken, useCreateToken, useUpdateToken, } from "../../model/hooks";
import { TOKEN_CATEGORIES, TOKEN_NETWORKS, TOKEN_REGIONS, } from "../../model/types/token.types";
var ACCENT = T.accent;
var ACCENT_BG = T.accentTint;
var ACCENT_BORDER = T.accentTintStrong;
var BORDER = T.border;
var DEFAULT_FORM = {
    name: "",
    symbol: "",
    description: "",
    category: "real-estate",
    country: "",
    region: "",
    zoneId: "",
    latitude: "",
    longitude: "",
    address: "",
    attributes: "",
    valueUsd: "",
    startingPrice: "",
    totalSupply: "",
    quoteCoinId: "",
    decimals: "18",
    network: "BSC",
    verified: false,
};
var inputSx = {
    "& .MuiOutlinedInput-root": {
        background: "rgba(255,255,255,0.02)",
        borderRadius: "12px",
        color: "#fff",
        "& fieldset": { borderColor: BORDER },
        "&:hover fieldset": { borderColor: ACCENT_BORDER },
        "&.Mui-focused fieldset": {
            borderColor: ACCENT,
            boxShadow: "0 0 0 3px ".concat(ACCENT_BG),
        },
        "& input": { color: "#fff" },
        "& textarea": { color: "#fff" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.45)" },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
    "& .MuiSelect-select": { color: "#fff" },
    "& .MuiSelect-icon": { color: "rgba(255,255,255,0.4)" },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.4)" },
};
var menuPaperSx = {
    background: "#1a2235",
    border: "1px solid ".concat(BORDER),
    "& .MuiMenuItem-root": {
        color: "#fff",
        "&:hover": { background: ACCENT_BG },
        "&.Mui-selected": { background: ACCENT_BG },
    },
};
var sectionTitleSx = {
    fontFamily: FONT.heading,
    fontSize: 15,
    fontWeight: 700,
    color: T.textPrimary,
    mb: 0.5,
};
export var TokenWizard = function () {
    var t = useTranslation().t;
    var id = useParams().id;
    var navigate = useNavigate();
    var isEdit = !!id;
    var _a = useState(DEFAULT_FORM), form = _a[0], setForm = _a[1];
    var _b = useState(null), imageFile = _b[0], setImageFile = _b[1];
    var _c = useState(null), imagePreview = _c[0], setImagePreview = _c[1];
    var _d = useState(null), error = _d[0], setError = _d[1];
    var imageInputRef = useRef(null);
    var _e = useAdminToken(id !== null && id !== void 0 ? id : ""), token = _e.data, loadingToken = _e.isLoading;
    var _f = useAdminCoins(), coins = _f.data, coinsLoading = _f.isLoading;
    var _g = useCreateToken(), createToken = _g.mutate, creating = _g.isPending;
    var _h = useUpdateToken(), updateToken = _h.mutate, updating = _h.isPending;
    var coinList = coins !== null && coins !== void 0 ? coins : [];
    var isDraft = !isEdit || (token === null || token === void 0 ? void 0 : token.status) === "DRAFT";
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!token)
            return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setForm({
            name: token.name,
            symbol: token.symbol,
            description: (_a = token.description) !== null && _a !== void 0 ? _a : "",
            category: token.category,
            country: (_b = token.country) !== null && _b !== void 0 ? _b : "",
            region: ((_c = token.region) !== null && _c !== void 0 ? _c : ""),
            zoneId: (_d = token.region) !== null && _d !== void 0 ? _d : "",
            latitude: token.latitude != null ? String(token.latitude) : "",
            longitude: token.longitude != null ? String(token.longitude) : "",
            address: (_e = token.address) !== null && _e !== void 0 ? _e : "",
            attributes: token.attributes ? JSON.stringify(token.attributes, null, 2) : "",
            valueUsd: token.valueUsd != null ? String(token.valueUsd) : "",
            startingPrice: (_f = token.startingPrice) !== null && _f !== void 0 ? _f : "",
            totalSupply: token.totalSupply != null ? String(token.totalSupply) : "",
            quoteCoinId: (_g = token.quoteCoinId) !== null && _g !== void 0 ? _g : "",
            decimals: String((_h = token.decimals) !== null && _h !== void 0 ? _h : 18),
            network: token.network,
            verified: token.verified,
        });
        if (token.logoUrl)
            setImagePreview(token.logoUrl);
    }, [token]);
    var setField = function (k) {
        return function (e) {
            return setForm(function (f) {
                var _a;
                return (__assign(__assign({}, f), (_a = {}, _a[k] = e.target.value, _a)));
            });
        };
    };
    var handleImage = function (file) {
        var _a;
        setImageFile(file);
        setImagePreview(file ? URL.createObjectURL(file) : (_a = token === null || token === void 0 ? void 0 : token.logoUrl) !== null && _a !== void 0 ? _a : null);
    };
    var validateAttributes = function () {
        if (!form.attributes.trim())
            return true;
        try {
            JSON.parse(form.attributes);
            return true;
        }
        catch (_a) {
            return false;
        }
    };
    var buildFormData = function () {
        var fd = new FormData();
        fd.append("name", form.name.trim());
        fd.append("symbol", form.symbol.trim().toUpperCase());
        fd.append("description", form.description);
        fd.append("category", form.category);
        fd.append("country", form.country);
        if (form.region)
            fd.append("region", form.region);
        if (form.zoneId)
            fd.append("zoneId", form.zoneId);
        fd.append("latitude", form.latitude);
        fd.append("longitude", form.longitude);
        fd.append("address", form.address);
        if (form.attributes.trim())
            fd.append("attributes", form.attributes.trim());
        fd.append("valueUsd", form.valueUsd);
        if (isDraft) {
            fd.append("startingPrice", form.startingPrice);
            fd.append("totalSupply", form.totalSupply);
        }
        if (form.quoteCoinId)
            fd.append("quoteCoinId", form.quoteCoinId);
        fd.append("decimals", form.decimals || "18");
        fd.append("network", form.network);
        fd.append("verified", form.verified ? "true" : "false");
        if (imageFile)
            fd.append("image", imageFile);
        return fd;
    };
    var handleSubmit = function () {
        setError(null);
        if (!form.name.trim() || !form.symbol.trim()) {
            setError(t("admin.tokens.form.nameSymbolRequired", "Name and symbol are required."));
            return;
        }
        if (!form.quoteCoinId) {
            setError(t("admin.tokens.form.quoteRequired", "Select the quote token."));
            return;
        }
        if (!validateAttributes()) {
            setError(t("admin.tokens.form.attributesInvalid", "Attributes must be valid JSON."));
            return;
        }
        var payload = buildFormData();
        var onError = function (e) {
            var _a, _b, _c;
            return setError((_c = (_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) !== null && _c !== void 0 ? _c : t("admin.tokens.form.saveFailed", "Failed to save the token."));
        };
        if (isEdit && id) {
            updateToken({ id: id, payload: payload }, { onSuccess: function (tk) { return navigate("/admin/tokens/".concat(tk.id)); }, onError: onError });
        }
        else {
            createToken(payload, {
                onSuccess: function (tk) { return navigate("/admin/tokens/".concat(tk.id)); },
                onError: onError,
            });
        }
    };
    var saving = creating || updating;
    if (isEdit && loadingToken) {
        return (_jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 8 }, children: _jsx(CircularProgress, { sx: { color: ACCENT } }) }));
    }
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 3, maxWidth: 920 }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }, children: [_jsx(Typography, { sx: { fontFamily: FONT.heading, fontSize: 24, fontWeight: 700, color: "#fff" }, children: isEdit
                            ? t("admin.tokens.form.editTitle", "Edit token")
                            : t("admin.tokens.form.createTitle", "Create token") }), _jsx(AdminButton, { variant: "secondary", icon: _jsx(ArrowBackIcon, {}), onClick: function () { return navigate(isEdit && id ? "/admin/tokens/".concat(id) : "/admin/tokens"); }, children: t("admin.tokens.back", "Back") })] }), error && (_jsx(Alert, { severity: "error", variant: "outlined", onClose: function () { return setError(null); }, children: error })), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 3 }, children: [_jsx(Box, { sx: { position: "relative", cursor: "pointer" }, onClick: function () { var _a; return (_a = imageInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, children: _jsx(Avatar, { src: imagePreview !== null && imagePreview !== void 0 ? imagePreview : undefined, sx: {
                                width: 96,
                                height: 96,
                                border: imagePreview ? "3px solid ".concat(ACCENT) : "2px dashed ".concat(BORDER),
                                background: "rgba(255,255,255,0.02)",
                            }, children: _jsx(AddPhotoAlternateIcon, { sx: { fontSize: 28, color: "rgba(255,255,255,0.25)" } }) }) }), _jsxs(Box, { children: [_jsx(Typography, { sx: { color: "rgba(255,255,255,0.55)", fontSize: 13 }, children: t("admin.tokens.form.logo", "Token logo") }), _jsx(Typography, { sx: { color: "rgba(255,255,255,0.3)", fontSize: 12, mt: 0.5 }, children: t("admin.tokens.form.logoHint", "Recommended 350 × 350 px · PNG or JPG") })] }), _jsx("input", { ref: imageInputRef, type: "file", accept: "image/png,image/jpeg,image/webp", style: { display: "none" }, onChange: function (e) {
                            var _a, _b;
                            handleImage((_b = (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : null);
                            e.target.value = "";
                        } })] }), _jsx(Typography, { sx: sectionTitleSx, children: t("admin.tokens.form.section.basics", "Basics") }), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }, children: [_jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.name", "Name"), value: form.name, onChange: setField("name"), sx: __assign(__assign({}, inputSx), { gridColumn: "1 / -1" }) }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.symbol", "Symbol"), value: form.symbol, onChange: function (e) { return setForm(function (f) { return (__assign(__assign({}, f), { symbol: e.target.value.toUpperCase() })); }); }, sx: inputSx }), _jsxs(FormControl, { fullWidth: true, sx: inputSx, children: [_jsx(InputLabel, { children: t("admin.tokens.form.network", "Network") }), _jsx(Select, { label: t("admin.tokens.form.network", "Network"), value: form.network, onChange: function (e) { return setForm(function (f) { return (__assign(__assign({}, f), { network: e.target.value })); }); }, MenuProps: { PaperProps: { sx: menuPaperSx } }, children: TOKEN_NETWORKS.map(function (n) { return (_jsx(MenuItem, { value: n.value, children: n.label }, n.value)); }) })] }), _jsxs(FormControl, { fullWidth: true, sx: inputSx, children: [_jsx(InputLabel, { children: t("admin.tokens.form.category", "Category") }), _jsx(Select, { label: t("admin.tokens.form.category", "Category"), value: form.category, onChange: function (e) { return setForm(function (f) { return (__assign(__assign({}, f), { category: e.target.value })); }); }, MenuProps: { PaperProps: { sx: menuPaperSx } }, children: TOKEN_CATEGORIES.map(function (c) { return (_jsx(MenuItem, { value: c.value, children: c.label }, c.value)); }) })] }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.description", "Description"), value: form.description, onChange: setField("description"), multiline: true, rows: 3, sx: __assign(__assign({}, inputSx), { gridColumn: "1 / -1" }) })] }), _jsx(Typography, { sx: sectionTitleSx, children: t("admin.tokens.form.section.geo", "Object & map location") }), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }, children: [_jsxs(FormControl, { fullWidth: true, sx: inputSx, children: [_jsx(InputLabel, { children: t("admin.tokens.form.region", "Region / Zone") }), _jsxs(Select, { label: t("admin.tokens.form.region", "Region / Zone"), value: form.region, onChange: function (e) {
                                    return setForm(function (f) { return (__assign(__assign({}, f), { region: e.target.value, zoneId: e.target.value })); });
                                }, MenuProps: { PaperProps: { sx: menuPaperSx } }, children: [_jsx(MenuItem, { value: "", children: "\u2014" }), TOKEN_REGIONS.map(function (r) { return (_jsx(MenuItem, { value: r.value, children: r.label }, r.value)); })] })] }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.country", "Country"), value: form.country, onChange: setField("country"), sx: inputSx }), _jsx(TextField, { fullWidth: true, type: "number", label: t("admin.tokens.form.latitude", "Latitude"), value: form.latitude, onChange: setField("latitude"), sx: inputSx }), _jsx(TextField, { fullWidth: true, type: "number", label: t("admin.tokens.form.longitude", "Longitude"), value: form.longitude, onChange: setField("longitude"), sx: inputSx }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.address", "Address"), value: form.address, onChange: setField("address"), sx: __assign(__assign({}, inputSx), { gridColumn: "1 / -1" }) }), _jsx(TextField, { fullWidth: true, type: "number", label: t("admin.tokens.form.valueUsd", "Estimated value (USD)"), value: form.valueUsd, onChange: setField("valueUsd"), sx: inputSx }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.attributes", "Attributes (JSON)"), placeholder: '{"area":"120 m\u00B2","yield":"7%"}', value: form.attributes, onChange: setField("attributes"), multiline: true, rows: 3, helperText: t("admin.tokens.form.attributesHint", "Area, yield, floor, rooms, etc. — valid JSON"), sx: __assign(__assign({}, inputSx), { gridColumn: "1 / -1" }) })] }), _jsx(Typography, { sx: sectionTitleSx, children: t("admin.tokens.form.section.economy", "Coin economy") }), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }, children: [_jsxs(FormControl, { fullWidth: true, required: true, sx: __assign(__assign({}, inputSx), { gridColumn: "1 / -1" }), children: [_jsx(InputLabel, { children: t("admin.tokens.form.quoteCoin", "Quote token") }), _jsxs(Select, { label: t("admin.tokens.form.quoteCoin", "Quote token"), value: coinList.some(function (c) { return c.id === form.quoteCoinId; }) ? form.quoteCoinId : "", onChange: function (e) { return setForm(function (f) { return (__assign(__assign({}, f), { quoteCoinId: e.target.value })); }); }, disabled: coinsLoading, MenuProps: { PaperProps: { sx: menuPaperSx } }, children: [coinsLoading && (_jsx(MenuItem, { value: "", disabled: true, children: t("admin.tokens.form.coinsLoading", "Loading coins…") })), !coinsLoading && coinList.length === 0 && (_jsx(MenuItem, { value: "", disabled: true, children: t("admin.tokens.form.coinsEmpty", "No coins found") })), coinList.map(function (coin) { return (_jsx(MenuItem, { value: coin.id, children: "".concat(coin.symbol, " \u2014 ").concat(coin.name) }, coin.id)); })] }), _jsx(FormHelperText, { children: t("admin.tokens.form.quoteCoinHint", "The pair this token trades against (e.g. QRE/USDT)") })] }), _jsx(TextField, { fullWidth: true, label: t("admin.tokens.form.startingPrice", "Starting price"), value: form.startingPrice, onChange: setField("startingPrice"), disabled: !isDraft, helperText: isDraft
                            ? t("admin.tokens.form.startingPriceHint", "Price of one coin at launch")
                            : t("admin.tokens.form.draftOnly", "Editable only while DRAFT"), sx: inputSx }), _jsx(TextField, { fullWidth: true, type: "number", label: t("admin.tokens.form.totalSupply", "Total supply"), value: form.totalSupply, onChange: setField("totalSupply"), disabled: !isDraft, helperText: isDraft
                            ? t("admin.tokens.form.totalSupplyHint", "Number of coins issued")
                            : t("admin.tokens.form.draftOnly", "Editable only while DRAFT"), sx: inputSx }), _jsx(TextField, { fullWidth: true, type: "number", label: t("admin.tokens.form.decimals", "Decimals"), value: form.decimals, onChange: setField("decimals"), sx: inputSx }), _jsxs(Box, { sx: {
                            display: "flex",
                            alignItems: "center",
                            background: "rgba(255,255,255,0.02)",
                            border: "1px solid ".concat(form.verified ? ACCENT_BORDER : BORDER),
                            borderRadius: "12px",
                            px: 2,
                            py: 1.25,
                        }, children: [_jsxs(Box, { flex: 1, children: [_jsx(Typography, { sx: { color: "#fff", fontWeight: 600, fontSize: 14 }, children: t("admin.tokens.verified", "Verified") }), _jsx(Typography, { sx: { color: "rgba(255,255,255,0.4)", fontSize: 12 }, children: t("admin.tokens.form.verifiedHint", "Trust checkmark on the public card") })] }), _jsx(FormControlLabel, { control: _jsx(Switch, { checked: form.verified, onChange: function (e) { return setForm(function (f) { return (__assign(__assign({}, f), { verified: e.target.checked })); }); }, sx: {
                                        "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: ACCENT },
                                    } }), label: "", sx: { m: 0 } })] })] }), _jsxs(Box, { sx: { display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }, children: [_jsx(Button, { onClick: function () { return navigate("/admin/tokens"); }, sx: { color: T.textSecondary }, children: t("admin.tokens.cancel", "Cancel") }), _jsx(AdminButton, { icon: saving ? (_jsx(CircularProgress, { size: 16, sx: { color: "inherit" } })) : isEdit ? (_jsx(PaidIcon, {})) : (_jsx(RocketLaunchIcon, {})), onClick: handleSubmit, disabled: saving, children: isEdit
                            ? t("admin.tokens.form.save", "Save changes")
                            : t("admin.tokens.form.createDraft", "Create draft") })] })] }));
};
